/** Deep-red closing CTA band + marketing footer. */
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LegalFooter } from '@/components/common/LegalFooter';
import { InstallAppLink } from '@/components/common/InstallPrompt';
import { BrandMark } from '@/components/landing/MarketingHeader';
import { marketingEvent } from '@/lib/marketing';

export function FinalCtaSection({ onBookDemo }) {
  return (
    <section className="bg-[#7A1F16] text-white scroll-mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="marketing-final-cta-heading">
          Ready to spend less time on restaurant admin?
        </h2>
        <p className="mt-4 text-base sm:text-lg text-white/80 max-w-xl mx-auto">
          Try RotaHQ free for 14 days and see how simple workforce management can be.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <Button asChild className="h-12 rounded-xl px-7 text-base bg-white text-[#7A1F16] hover:bg-[rgba(255,255,255,0.92)] font-semibold transition-colors duration-150">
            <Link to="/signup" onClick={() => marketingEvent('click_start_trial', { placement: 'final_cta' })} data-testid="marketing-final-cta-start-trial-button">
              Start Your Free Trial
            </Link>
          </Button>
          <Button
            variant="outline"
            className="h-12 rounded-xl px-7 text-base bg-transparent border-[rgba(255,255,255,0.4)] text-white hover:bg-[rgba(255,255,255,0.1)] hover:text-white font-semibold transition-colors duration-150"
            onClick={() => onBookDemo('final_cta')}
            data-testid="marketing-final-cta-book-demo-button"
          >
            Book a Demo
          </Button>
        </div>
        <p className="mt-5 text-sm text-white/65">14-day free trial · No setup fee · No per-employee pricing</p>
      </div>
    </section>
  );
}

export function MarketingFooter() {
  return (
    <footer className="bg-[color:var(--brand-bg)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-start justify-between gap-6">
        <div>
          <BrandMark size="sm" />
          <p className="mt-2 text-sm text-[color:var(--brand-text-muted)] max-w-xs">
            Staff rotas, attendance and payroll for UK restaurants.
          </p>
          <div className="mt-4 space-y-2">
            <a
              href="mailto:htet@rotahq.uk"
              className="inline-flex items-center gap-2 text-sm text-[color:var(--brand-text)] hover:text-[color:var(--brand-primary)] transition-colors duration-150"
              data-testid="marketing-footer-contact-email"
            >
              <Mail className="h-4 w-4 text-[color:var(--brand-primary)]" aria-hidden="true" />
              htet@rotahq.uk
            </a>
            <div>
              <a
                href="tel:+447586698858"
                className="inline-flex items-center gap-2 text-sm text-[color:var(--brand-text)] hover:text-[color:var(--brand-primary)] transition-colors duration-150"
                data-testid="marketing-footer-contact-phone"
              >
                <Phone className="h-4 w-4 text-[color:var(--brand-primary)]" aria-hidden="true" />
                +44 7586 698858
              </a>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://www.facebook.com/rotahq/"
                target="_blank"
                rel="noreferrer"
                aria-label="RotaHQ on Facebook"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--brand-border)] bg-white text-[color:var(--brand-text-muted)] hover:text-[color:var(--brand-primary)] hover:border-[color:var(--brand-primary)]/40 transition-colors duration-150"
                data-testid="marketing-footer-facebook-link"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/rotahq/"
                target="_blank"
                rel="noreferrer"
                aria-label="RotaHQ on Instagram"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--brand-border)] bg-white text-[color:var(--brand-text-muted)] hover:text-[color:var(--brand-primary)] hover:border-[color:var(--brand-primary)]/40 transition-colors duration-150"
                data-testid="marketing-footer-instagram-link"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Footer">
          <a href="#features" className="hover:text-[color:var(--brand-primary)] transition-colors duration-150" data-testid="marketing-footer-features-link">Features</a>
          <a href="#pricing" className="hover:text-[color:var(--brand-primary)] transition-colors duration-150" data-testid="marketing-footer-pricing-link">Pricing</a>
          <a href="#faq" className="hover:text-[color:var(--brand-primary)] transition-colors duration-150" data-testid="marketing-footer-faq-link">FAQ</a>
          <Link to="/login" className="hover:text-[color:var(--brand-primary)] transition-colors duration-150" data-testid="marketing-footer-login-link">Log In</Link>
          <Link to="/signup" className="hover:text-[color:var(--brand-primary)] transition-colors duration-150" data-testid="marketing-footer-signup-link">Start Free Trial</Link>
          <InstallAppLink
            testId="marketing-footer-install-link"
            className="inline-flex items-center gap-1.5 hover:text-[color:var(--brand-primary)] transition-colors duration-150"
          />
        </nav>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
        <LegalFooter />
      </div>
    </footer>
  );
}
